import * as React from 'react'
import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { previewInvite, acceptInvite } from '@/features/invite/api'
import { useAuth } from '@/features/auth/AuthContext'
import { useToast } from '@/components/feedback/ToastProvider'
import { ROLE_LABELS } from '@/features/auth/types'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/primitives/Button'
import { Input } from '@/components/primitives/Input'
import { FormField } from '@/components/primitives/FormField'
import { Spinner } from '@/components/feedback/Spinner'
import './AcceptInvitePage.css'

function Brand(): React.JSX.Element {
  return (
    <span className="invite-brand">
      <span className="invite-brand-mark" aria-hidden="true">
        <svg viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="2.5" opacity="0.9" />
          <path d="M9 21h4l2.5-7 4 13 3-9h8.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span className="invite-brand-text">
        <strong>eCareLTC</strong> Vitals
      </span>
    </span>
  )
}

export function AcceptInvitePage(): React.JSX.Element {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const toast = useToast()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [showPw, setShowPw] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const preview = useQuery({
    queryKey: ['invite', token],
    queryFn: () => previewInvite(token),
    enabled: token.length > 0,
    retry: false,
  })

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setFormError(null)
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setFormError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      await acceptInvite(token, password)
      await refresh() // cookies are set; load the session
      toast.success('Welcome aboard', 'Your account is ready.')
      navigate('/', { replace: true })
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : 'Could not accept the invitation.'
      setFormError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  let body: React.JSX.Element

  if (!token) {
    body = <InviteError message="This invitation link is missing its token." />
  } else if (preview.isLoading) {
    body = (
      <div className="invite-loading">
        <Spinner /> <span>Checking your invitation…</span>
      </div>
    )
  } else if (preview.isError || !preview.data) {
    const msg =
      preview.error instanceof ApiError
        ? preview.error.message
        : 'This invitation is no longer valid.'
    body = <InviteError message={msg} />
  } else {
    const inv = preview.data
    body = (
      <>
        <h1 className="invite-title">Set your password</h1>
        <p className="invite-sub">
          You've been invited to <strong>{inv.org_name}</strong> as a{' '}
          {ROLE_LABELS[inv.role]}. Signing in as <strong>{inv.email}</strong>.
        </p>

        <form className="invite-form" onSubmit={(e) => void onSubmit(e)} noValidate>
          <FormField label="New password" htmlFor="password" required>
            <div className="pw-wrap">
              <Input
                id="password"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                autoFocus
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button type="button" className="pw-eye" onClick={() => setShowPw(v => !v)} tabIndex={-1} aria-label={showPw ? 'Hide password' : 'Show password'}>
                {showPw ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </FormField>
          <FormField label="Confirm password" htmlFor="confirm" required>
            <div className="pw-wrap">
              <Input
                id="confirm"
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Re-enter your password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              <button type="button" className="pw-eye" onClick={() => setShowConfirm(v => !v)} tabIndex={-1} aria-label={showConfirm ? 'Hide password' : 'Show password'}>
                {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </FormField>

          {formError ? (
            <p className="invite-error" role="alert">
              {formError}
            </p>
          ) : null}

          <Button type="submit" loading={submitting} className="invite-submit">
            Set password &amp; sign in
          </Button>
        </form>
      </>
    )
  }

  return (
    <div className="invite-page">
      <div className="invite-card">
        <Brand />
        {body}
      </div>
    </div>
  )
}

function InviteError({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="invite-state">
      <h1 className="invite-title">Invitation problem</h1>
      <p className="invite-sub">{message}</p>
      <p className="invite-sub-small">
        Ask whoever invited you to send a fresh invitation.
      </p>
    </div>
  )
}

function EyeIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  )
}
function EyeOffIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  )
}
